import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

// Services
import { userService } from '../../services/users';
import { artistService } from '../../services/artist';
import { recordingsService } from '../../services/recordings';
import {
  extractSpotifyTrackId as extractSpotifyTrackIdFromTrack,
  hydrateTracksWithMatches,
} from '../../utils/trackMatches';
import spotifyCacheService from '../../services/spotifyCache';
import { isSpotifyRequestAllowed } from '../../utils/spotify/requestWindow';

// Interfaces
import type { Album } from '../../interfaces/albums';
import type { Pagination } from '../../interfaces/api';
import type { Artist } from '../../interfaces/artist';
import type { Track, TrackWithSave } from '../../interfaces/track';
import { RootState } from '../store';

const createEmptyPagination = <T>(): Pagination<T> => ({
  href: '',
  items: [],
  limit: 0,
  offset: 0,
  previous: null,
  next: null,
  total: 0,
});

const initialState: {
  albums: Album[];
  singles: Album[];
  appearsOn: Album[];
  compilations: Album[];

  otherArtists: Artist[];

  artist: Artist | null;
  topTracks: TrackWithSave[];

  loading: boolean;
  following: boolean;
} = {
  topTracks: [],
  albums: [],
  artist: null,
  otherArtists: [],

  loading: true,
  following: false,
  singles: [],
  appearsOn: [],
  compilations: [],
};

export const fetchArtist = createAsyncThunk<[Artist, boolean, TrackWithSave[], Album[][]], string>(
  'artist/fetchArtist',
  async (id, params) => {
    const state = params.getState() as RootState;
    const user = state.auth.user;
    const spotifyRequestsAllowed = isSpotifyRequestAllowed({ reason: 'artistSlice.fetchArtist' });

    let artist: Artist | null = null;
    let primaryError: unknown = null;

    if (spotifyRequestsAllowed) {
      try {
        const response = await artistService.fetchArtist(id);
        artist = response.data as Artist;
      } catch (error) {
        primaryError = error;
        if (process.env.NODE_ENV === 'development') {
          console.warn('[artistSlice] Failed to load artist from Spotify API, attempting cache fallback', {
            id,
            error,
          });
        }
      }
    } else if (process.env.NODE_ENV === 'development') {
      console.info('[artistSlice] Spotify artist requests blocked by schedule', { id });
    }

    if (!artist) {
      try {
        artist = await spotifyCacheService.fetchArtist(id);
      } catch (cacheError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[artistSlice] Cache fallback failed for artist', { id, cacheError });
        }
      }

      if (!artist) {
        if (primaryError) {
          throw primaryError;
        }
        throw new Error('Artist not found');
      }
    }

    const followingPromise = user
      ? userService.checkFollowingArtists([id]).catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[artistSlice] Failed to resolve following state', { id, error });
          }
          return { data: [false] } as { data: boolean[] };
        })
      : Promise.resolve({ data: [false] } as { data: boolean[] });

    const topTracksPromise = spotifyRequestsAllowed
      ? artistService.fetchArtistTopTracks(id).catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[artistSlice] Failed to load artist top tracks', { id, error });
          }
          return { data: { tracks: [] } } as { data: { tracks: Track[] } };
        })
      : Promise.resolve({ data: { tracks: [] } } as { data: { tracks: Track[] } });

    const createEmptyAlbumResponse = () => ({ data: createEmptyPagination<Album>() });

    const albumGroupsPromise = spotifyRequestsAllowed
      ? Promise.all([
          artistService.fetchArtistAlbums(id, { limit: 10, include_groups: 'album' }).catch((error) => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[artistSlice] Failed to load artist albums (album group)', { id, error });
            }
            return createEmptyAlbumResponse();
          }),
          artistService.fetchArtistAlbums(id, { limit: 10, include_groups: 'single' }).catch((error) => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[artistSlice] Failed to load artist albums (single group)', { id, error });
            }
            return createEmptyAlbumResponse();
          }),
          artistService.fetchArtistAlbums(id, { limit: 10, include_groups: 'appears_on' }).catch((error) => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[artistSlice] Failed to load artist albums (appears_on group)', { id, error });
            }
            return createEmptyAlbumResponse();
          }),
          artistService.fetchArtistAlbums(id, { limit: 10, include_groups: 'compilation' }).catch((error) => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[artistSlice] Failed to load artist albums (compilation group)', { id, error });
            }
            return createEmptyAlbumResponse();
          }),
        ])
      : Promise.resolve([
          createEmptyAlbumResponse(),
          createEmptyAlbumResponse(),
          createEmptyAlbumResponse(),
          createEmptyAlbumResponse(),
        ]);

    const [followingResponse, topTracksResponse, albumGroupResponses] = await Promise.all([
      followingPromise,
      topTracksPromise,
      albumGroupsPromise,
    ]);

    const [albumsResponse, singlesResponse, appearsOnResponse, compilationsResponse] = albumGroupResponses;

    const [following] = followingResponse.data as boolean[];

    const tracks = (topTracksResponse.data as any).tracks as Track[];
    const albums = (albumsResponse.data as Pagination<Album>).items as Album[];
    const singles = (singlesResponse.data as Pagination<Album>).items as Album[];
    const appearsOn = (appearsOnResponse.data as Pagination<Album>).items as Album[];
    const compilations = (compilationsResponse.data as Pagination<Album>).items as Album[];

    const extraResponses = await Promise.all([
      userService.checkSavedTracks(tracks.map((track) => track.id)).catch(() => ({ data: [] })),
    ]);

    const saved = extraResponses[0].data as boolean[];
    const itemsWithSave: TrackWithSave[] = tracks.map((item, index) => ({
      ...item,
      saved: saved[index],
    }));

    let enrichedTracks = itemsWithSave;
    if (itemsWithSave.length) {
      const trackIds = itemsWithSave
        .map((track) => extractSpotifyTrackIdFromTrack(track))
        .filter((trackId): trackId is string => typeof trackId === 'string' && trackId.length > 0);

      if (trackIds.length) {
        const matches = await recordingsService.fetchTrackMatches(trackIds);
        enrichedTracks = hydrateTracksWithMatches(itemsWithSave, matches);
      }
    }

    return [artist, following, enrichedTracks, [albums, singles, appearsOn, compilations]];
  }
);

const fetchOtherArtists = createAsyncThunk<Artist[], string>(
  'artist/fetchOtherArtists',
  async (id) => {
    if (!isSpotifyRequestAllowed({ reason: 'artistSlice.fetchOtherArtists' })) {
      if (process.env.NODE_ENV === 'development') {
        console.info('[artistSlice] Skipping related artists fetch due to blocked window', { id });
      }
      return [];
    }
    try {
      const response = await artistService.fetchSimilarArtists(id);
      return response.data.artists;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[artistSlice] Failed to load related artists', { id, error });
      }
      return [];
    }
  }
);

const artistSlice = createSlice({
  name: 'artist',
  initialState,
  reducers: {
    setFollowing(state, action: PayloadAction<{ following: boolean }>) {
      state.following = action.payload.following;
    },
    setArtist(state, action: PayloadAction<{ artist: Artist | null }>) {
      state.artist = action.payload.artist;
      if (!action.payload.artist) {
        state.albums = [];
        state.singles = [];
        state.appearsOn = [];
        state.compilations = [];
        state.topTracks = [];
        state.following = false;
        state.loading = true;
        state.artist = null;
        state.otherArtists = [];
      }
    },
    setTopSongLikeState(state, action: PayloadAction<{ id: string; saved: boolean }>) {
      state.topTracks = state.topTracks.map((track) =>
        track.id === action.payload.id ? { ...track, saved: action.payload.saved } : track
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchArtist.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchArtist.fulfilled, (state, action) => {
      state.artist = action.payload[0];
      state.following = action.payload[1];
      state.topTracks = action.payload[2];
      state.albums = action.payload[3][0];
      state.singles = action.payload[3][1];
      state.appearsOn = action.payload[3][2];
      state.compilations = action.payload[3][3];
      state.loading = false;
    });
    builder.addCase(fetchOtherArtists.fulfilled, (state, action) => {
      state.otherArtists = action.payload;
    });
  },
});

export const artistActions = {
  fetchArtist,
  fetchOtherArtists,
  ...artistSlice.actions,
};

export default artistSlice.reducer;
